import {
  AdminInput,
  AdminSpinner,
  BrandImageComponent,
  Button,
  Div,
  ErrorToast,
  ListSpinner,
  ModalPortal,
  P,
  PropsModal,
  Radio,
  SuccessToast,
  createBrandWithTemplate,
  getBrandTemplates,
  lightTheme,
  reportError,
  restaurantImage,
} from '@vgrubs/components';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { CreateBrandWithTemplateRequestBody } from 'components/src/types/api/brand.types';
import { Brand, BrandTemplateEntity } from 'components/src/types/brand.types';
import { LocationEntity } from 'components/src/types/location.types';
import debounce from 'lodash.debounce';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export const TemplateBrandAdd = ({
  modalIsOpen,
  closeModal,
  setData,
  data,
  type = 'add',
  onCreateNonVGrubsBrand,
  onAddBrandSuccess,
}: PropsModal & {
  modalIsOpen: boolean;
  setData: Dispatch<SetStateAction<LocationEntity | null>>;
  data: LocationEntity;
  type?: 'add' | 'edit';
  onCreateNonVGrubsBrand: () => void;
  onAddBrandSuccess: (brand: Brand | null) => void;
}) => {
  const [template, setTemplate] = useState<number | null>(null);
  const [brandTemplates, setBrandTemplates] = useState<BrandTemplateEntity[]>(
    []
  );
  // `loading` covers the first page, a new search and the submit;
  // `loadingMore` is the next page arriving at the bottom of the list.
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const emptyData = () => {
    setTemplate(null);
  };

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [ref, inView] = useInView();
  const [query, setQuery] = useState('');

  async function getAllLocations(search, page, hasMore) {
    if (!hasMore) return;

    try {
      if (search) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const { data } = await getBrandTemplates({
        limit: 20,
        page: page,
        query: query?.length > 0 ? query : null,
      });

      if (search) {
        setBrandTemplates(() => [...data.data]);
        setHasMore(data.data.length > 0);
      } else if (data.data[0]) {
        setHasMore(true);
        setBrandTemplates((prevItems) => [...prevItems, ...data.data]);
      } else {
        setHasMore(false);
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    setTemplate(null);
    setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);

  useEffect(() => {
    if (modalIsOpen) {
      setHasMore(true);
      setPage(1);
      getAllLocations(true, 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, modalIsOpen]);

  useEffect(() => {
    if (inView && brandTemplates.length > 0) {
      getAllLocations(false, page, hasMore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const handleChange = async (e) => {
    setQuery(e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  async function handleAdd() {
    if (!template || !data) return;
    const brand = {
      templateId: template,
      locationId: data.id,
    } as CreateBrandWithTemplateRequestBody;

    try {
      setLoading(true);
      const res = await createBrandWithTemplate(brand);
      SuccessToast(res.data.message);
      closeModal?.();

      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, brands: [res.data.data, ...prev.brands] };
      });

      onAddBrandSuccess(res.data.data);
      emptyData();
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  function handleSubmit() {
    if (!template) return ErrorToast('Please select Brand Template');
    handleAdd();
  }

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalPortal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header={`${type === 'edit' ? 'Edit' : 'Add'} Brand`}
        subtitle="Choose vGrubs Brand"
        $numOfFloatingButtons={2}
        footer={
          <>
            <Button $mt="40" $w="100%" $selected onClick={handleSubmit}>
              Submit
            </Button>
            <Button
              $gray
              $selected
              $w="100%"
              $mt="16"
              $mtSM="16"
              onClick={onCreateNonVGrubsBrand}
            >
              Create Non vGrubs Brand
            </Button>
          </>
        }
      >
        <AdminInput
          maxWidth="100%"
          maxWidthSM="100%"
          search
          placeholder="Search Brand"
          onChange={debouncedResults}
          mb="20"
        />

        {brandTemplates.map(({ imageUrl, id, name }) => {
          const isSelected = template === id;
          return (
            <Div
              key={id}
              $box
              $p="12px 24px 12px 16px"
              $m="8px 0"
              $flex
              $between
              $alignCenter
              $gap="16px"
              $pointer
              onClick={() => setTemplate(id)}
            >
              <Div $flex $alignCenter $gap="16px">
                <Div $flex $flexNone>
                  <BrandImageComponent
                    image={imageUrl}
                    height={50}
                    width={50}
                  />
                </Div>
                <P
                  $bold
                  $m="0"
                  $color={isSelected ? lightTheme.primary : undefined}
                >
                  {name}
                </P>
              </Div>
              <Radio checked={isSelected} />
            </Div>
          );
        })}

        {brandTemplates.length === 0 && !loading && (
          <NoDataComponent
            image={restaurantImage}
            description="No brands were found"
            height="200px"
            heightSM="200px"
            imageWidth="100px"
            imageWidthSM="80px"
          />
        )}

        <ListSpinner loading={loadingMore} observerRef={ref} mb="0" mbSM="0" />
      </ModalPortal>
    </>
  );
};
